import { useEffect } from "react";

// Hook de SEO enriquecido para páginas públicas.
// Ajusta title, meta description, Open Graph, Twitter Card, canonical, robots,
// hreflang, keywords e inyecta JSON-LD (datos estructurados) siguiendo las guías
// de Google Search Central, Bing Webmaster y Yahoo.
export const SITE_URL = "https://bhgcnexuslogistics.com";
export const SITE_NAME = "BHGC Nexus Logistics";
const DEFAULT_OG_IMAGE = "https://media.base44.com/images/public/6aa6211001d513cafb0a3a5c/7c1a1ce9b_generated_image.png";
const DEFAULT_ROBOTS = "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1";

export function useSeo({ title, description, canonical, type = "website", image = DEFAULT_OG_IMAGE, imageAlt, keywords, robots = DEFAULT_ROBOTS, jsonLd = [] }) {
  useEffect(() => {
    const prevTitle = document.title;
    if (title) document.title = title;

    const ensure = (selector, create) => {
      let el = document.querySelector(selector);
      if (!el) { el = create(); document.head.appendChild(el); }
      return el;
    };
    const setMeta = (selector, attr, value, content) => {
      const el = ensure(selector, () => { const m = document.createElement("meta"); m.setAttribute(attr, value); return m; });
      el.setAttribute("content", content);
    };

    if (description) {
      setMeta('meta[name="description"]', "name", "description", description);
      setMeta('meta[property="og:description"]', "property", "og:description", description);
      setMeta('meta[name="twitter:description"]', "name", "twitter:description", description);
    }
    if (title) {
      setMeta('meta[property="og:title"]', "property", "og:title", title);
      setMeta('meta[name="twitter:title"]', "name", "twitter:title", title);
    }
    setMeta('meta[property="og:type"]', "property", "og:type", type);
    setMeta('meta[property="og:locale"]', "property", "og:locale", "es_MX");
    setMeta('meta[property="og:site_name"]', "property", "og:site_name", SITE_NAME);
    setMeta('meta[property="og:image"]', "property", "og:image", image);
    setMeta('meta[property="og:image:width"]', "property", "og:image:width", "1200");
    setMeta('meta[property="og:image:height"]', "property", "og:image:height", "630");
    setMeta('meta[property="og:image:alt"]', "property", "og:image:alt", imageAlt || (title ? `${SITE_NAME} — ${title}` : SITE_NAME));
    setMeta('meta[name="twitter:image"]', "name", "twitter:image", image);
    setMeta('meta[name="twitter:card"]', "name", "twitter:card", "summary_large_image");
    if (keywords) setMeta('meta[name="keywords"]', "name", "keywords", keywords);
    setMeta('meta[name="robots"]', "name", "robots", robots);
    setMeta('meta[name="googlebot"]', "name", "googlebot", robots);

    const altLinks = [];
    if (canonical) {
      ensure('link[rel="canonical"]', () => { const l = document.createElement("link"); l.rel = "canonical"; return l; }).href = canonical;
      setMeta('meta[property="og:url"]', "property", "og:url", canonical);
      // hreflang: contenido en español para México + x-default
      const mkAlt = (hreflang, href) => { const l = document.createElement("link"); l.rel = "alternate"; l.hreflang = hreflang; l.href = href; document.head.appendChild(l); altLinks.push(l); };
      mkAlt("es-mx", canonical);
      mkAlt("x-default", canonical);
    }

    const scripts = [];
    jsonLd.forEach((data) => {
      const s = document.createElement("script");
      s.type = "application/ld+json";
      s.dataset.seoKey = "useSeo";
      s.textContent = JSON.stringify(data);
      document.head.appendChild(s);
      scripts.push(s);
    });

    return () => {
      document.title = prevTitle;
      scripts.forEach(s => s.remove());
      altLinks.forEach(l => l.remove());
    };
  }, [title, description, canonical, type, image, keywords, robots, JSON.stringify(jsonLd)]);
}

// Helper para datos estructurados de BreadcrumbList.
export function breadcrumbJsonLd(crumbs) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: crumbs.map((c, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: c.name,
      item: c.url,
    })),
  };
}

// Helper para datos estructurados de FAQPage (válido para Google, Bing y búsquedas IA).
export function faqJsonLd(items) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: items.map(f => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };
}

// Helper para datos estructurados de ItemList (listado de carriers/recursos para
// carrusel y resultados enriquecidos en móvil y escritorio).
export function itemListJsonLd({ name, url, items }) {
  return {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name,
    url,
    itemListElement: items.map((it, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: it.name,
      url: it.url,
      ...(it.description ? { description: it.description } : {}),
    })),
  };
}

// Helper para datos estructurados de WebPage con sección "speakable" (ideal para
// resultados enriquecidos y asistentes de voz) y entidad publicadora enlazada.
export function webPageJsonLd({ name, description, url, breadcrumbs }) {
  const graph = [
    {
      "@type": "WebPage",
      "@id": `${url}#webpage`,
      name,
      description,
      url,
      inLanguage: "es-MX",
      isPartOf: { "@type": "WebSite", "@id": `${SITE_URL}/#website`, name: SITE_NAME, url: SITE_URL },
      publisher: { "@type": "Organization", "@id": `${SITE_URL}/#organization`, name: SITE_NAME, url: SITE_URL },
    },
  ];
  if (breadcrumbs && breadcrumbs.length) {
    graph.push(breadcrumbJsonLd(breadcrumbs));
  }
  return { "@context": "https://schema.org", "@graph": graph };
}

// Helper para datos estructurados de Product (schema.org) — ficha de producto
// enriquecida para Google Shopping, resultados comerciales y comparadores.
export function productJsonLd({ name, description, image, url, price, currency = "MXN", inStock = true, sku, brand = SITE_NAME, category }) {
  return {
    "@context": "https://schema.org",
    "@type": "Product",
    name,
    ...(description ? { description } : {}),
    ...(image ? { image } : {}),
    ...(category ? { category } : {}),
    brand: { "@type": "Brand", name: brand },
    offers: {
      "@type": "Offer",
      url,
      price: String(price),
      priceCurrency: currency,
      availability: inStock ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
      ...(sku ? { sku } : {}),
    },
  };
}